import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { useAuthStore } from './auth-store';
import { logActivity } from '@/lib/activity-logger';

export interface ShoppingList {
  id: string;
  name: string;
  description?: string;
  store?: string;
  isPrimary?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ShoppingListItem {
  id: string;
  listId: string;
  name: string;
  category?: string;
  notes?: string;
  link?: string;
  quantity: number;
  price?: number;
  store?: string;
  aisle?: string;
  purchased: boolean;
  purchasedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ShoppingListState {
  lists: ShoppingList[];
  items: ShoppingListItem[];
  isSyncing: boolean;
  fetchFromServer: (familyId?: string) => Promise<void>;
  createList: (data: { name: string; description?: string; store?: string; isPrimary?: boolean }) => Promise<ShoppingList | null>;
  updateList: (id: string, updates: { name?: string; description?: string; store?: string; isPrimary?: boolean }) => Promise<void>;
  deleteList: (id: string) => Promise<void>;
  createItem: (data: { listId: string; name: string; category?: string; notes?: string; link?: string; quantity?: number; price?: number; store?: string; aisle?: string; purchased?: boolean }) => Promise<void>;
  updateItem: (id: string, updates: { listId?: string; name?: string; category?: string; notes?: string; link?: string; quantity?: number; price?: number; store?: string; aisle?: string; purchased?: boolean }) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  togglePurchased: (id: string) => Promise<void>;
  clearPurchasedForList: (listId: string) => Promise<void>;
  getItemsForList: (listId: string) => ShoppingListItem[];
}

export const useShoppingListStore = create<ShoppingListState>()(
  persist(
    (set, get) => ({
      lists: [],
      items: [],
      isSyncing: false,

      fetchFromServer: async (familyId?: string) => {
        set({ isSyncing: true });
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const resolved = familyId || ownerId || useAuthStore.getState().user?.familyId;
        const qs = resolved ? `?familyId=${encodeURIComponent(resolved)}` : '';
        const res = await apiGet<{ lists: ShoppingList[]; items: ShoppingListItem[] }>(`/shopping-lists/get${qs}`);
        if (res.success && res.data) {
          set({ lists: res.data.lists || [], items: res.data.items || [], isSyncing: false });
        } else {
          set({ isSyncing: false });
        }
      },

      createList: async (data) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId2 = getEffectiveFamilyOwnerId();
        const familyId2 = ownerId2 || useAuthStore.getState().user?.familyId;
        const qs2 = familyId2 ? `?familyId=${encodeURIComponent(familyId2)}` : '';
        const res = await apiPost<{ list: ShoppingList }>(`/shopping-lists/create${qs2}`, data);
        if (res.success && res.data?.list) {
          logActivity({ action: 'ADD_SHOPPING_ITEM', category: 'shopping', description: 'Shopping list created', metadata: { listId: res.data.list.id, name: data.name } });
          // If the new list is marked as primary, ask the server for authoritative
          // data so the previous primary is demoted and ordering is correct.
          if (data.isPrimary) {
            await get().fetchFromServer(familyId2 as string | undefined);
            return res.data.list;
          }

          set((state) => ({ lists: [...state.lists, res.data!.list] }));
          return res.data.list;
        }
        return null;
      },

      updateList: async (id, updates) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId3 = getEffectiveFamilyOwnerId();
        const familyId3 = ownerId3 || useAuthStore.getState().user?.familyId;
        const qs3 = familyId3 ? `?familyId=${encodeURIComponent(familyId3)}` : '';
        const res = await apiPut<{ list: ShoppingList }>(`/shopping-lists/update/${id}${qs3}`, updates);
        if (res.success && res.data?.list) {
          logActivity({ action: 'UPDATE_SHOPPING_ITEM', category: 'shopping', description: 'Shopping list updated', metadata: { listId: id } });
          // Om isPrimary uppdateras, hämta all data för att säkerställa korrekt synkning av alla listor
          if (updates.isPrimary !== undefined) {
            await get().fetchFromServer();
          } else {
            set((state) => ({ lists: state.lists.map(l => l.id === id ? res.data!.list : l) }));
          }
        }
      },

      deleteList: async (id) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId4 = getEffectiveFamilyOwnerId();
        const familyId4 = ownerId4 || useAuthStore.getState().user?.familyId;
        const qs4 = familyId4 ? `?familyId=${encodeURIComponent(familyId4)}` : '';
        const res = await apiDelete(`/shopping-lists/delete/${id}${qs4}`);
        if (res.success) {
          logActivity({ action: 'DELETE_SHOPPING_ITEM', category: 'shopping', description: 'Shopping list deleted', metadata: { listId: id } });
          set((state) => ({ 
            lists: state.lists.filter(l => l.id !== id),
            items: state.items.filter(item => item.listId !== id)
          }));
        }
      },

      createItem: async (data) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId5 = getEffectiveFamilyOwnerId();
        const familyId5 = ownerId5 || useAuthStore.getState().user?.familyId;
        const qs5 = familyId5 ? `?familyId=${encodeURIComponent(familyId5)}` : '';
        const res = await apiPost<{ item: ShoppingListItem }>(`/shopping-lists/items/create${qs5}`, data);
        if (res.success && res.data?.item) {
          logActivity({ action: 'ADD_SHOPPING_ITEM', category: 'shopping', description: 'Shopping item added', metadata: { itemId: res.data.item.id, name: data.name } });
          set((state) => ({ items: [...state.items, res.data!.item] }));
        }
      },

      updateItem: async (id, updates) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId6 = getEffectiveFamilyOwnerId();
        const familyId6 = ownerId6 || useAuthStore.getState().user?.familyId;
        const qs6 = familyId6 ? `?familyId=${encodeURIComponent(familyId6)}` : '';
        const res = await apiPut<{ item: ShoppingListItem }>(`/shopping-lists/items/update/${id}${qs6}`, updates);
        if (res.success && res.data?.item) {
          logActivity({ action: 'UPDATE_SHOPPING_ITEM', category: 'shopping', description: 'Shopping item updated', metadata: { itemId: id } });
          set((state) => ({ items: state.items.map(i => i.id === id ? res.data!.item : i) }));
        }
      },

      deleteItem: async (id) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId7 = getEffectiveFamilyOwnerId();
        const familyId7 = ownerId7 || useAuthStore.getState().user?.familyId;
        const qs7 = familyId7 ? `?familyId=${encodeURIComponent(familyId7)}` : '';
        const res = await apiDelete(`/shopping-lists/items/delete/${id}${qs7}`);
        if (res.success) {
          logActivity({ action: 'DELETE_SHOPPING_ITEM', category: 'shopping', description: 'Shopping item deleted', metadata: { itemId: id } });
          set((state) => ({ items: state.items.filter(i => i.id !== id) }));
        }
      },

      clearPurchasedForList: async (listId) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId8 = getEffectiveFamilyOwnerId();
        const familyId8 = ownerId8 || useAuthStore.getState().user?.familyId;
        const qs8 = familyId8 ? `?familyId=${encodeURIComponent(familyId8)}` : '';
        const res = await apiPost<{ success: boolean }>(`/shopping-lists/clear-purchased${qs8}`, { listId });
        if (res.success) {
          set((state) => ({ items: state.items.filter(i => !(i.listId === listId && i.purchased)) }));
        }
      },

      togglePurchased: async (id) => {
        const item = get().items.find(i => i.id === id);
        if (!item) return;
        const newPurchased = !item.purchased;
        const purchasedAt = newPurchased ? new Date().toISOString() : undefined;
        
        // Optimistisk uppdatering
        set((state) => ({ 
          items: state.items.map(i => 
            i.id === id 
              ? { ...i, purchased: newPurchased, purchasedAt, updatedAt: new Date().toISOString() }
              : i
          ) 
        }));
        
        // Synka med servern (inkludera familyId om relevant)
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const familyId = ownerId || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';

        const res = await apiPut<{ item: ShoppingListItem }>(`/shopping-lists/items/update/${id}${qs}`, { 
          purchased: newPurchased,
          purchasedAt,
        });
        
        // Om servern returnerar data, använd den (för att säkerställa synk)
        if (res.success && res.data?.item) {
          set((state) => ({ items: state.items.map(i => i.id === id ? res.data!.item : i) }));
        } else if (!res.success) {
          // Om det misslyckas, återställ till ursprungligt tillstånd
          set((state) => ({ items: state.items.map(i => i.id === id ? item : i) }));
        }
      },

      getItemsForList: (listId) => {
        return get().items.filter(item => item.listId === listId);
      },
    }),
    {
      name: 'shopping-lists-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useShoppingListStore;
